/**
 * Kurulum — "bu sistem fatura kesebilir mi, değilse neden?"
 *
 * Kullanıcının koyduğu kural: ayarlar ve bağlantılar tamamlanmadan **asla**
 * fatura gönderilmez; eksik ne ise doğru bilgilendirilip düzelttirilir.
 *
 * Bu ekran o kuralın yüzü. Veri kaynağı hazırlık kapısıyla AYNI
 * (`settings/setup.ts`): worker'ın iş almayı reddettiği sebep ile burada
 * görünen eksik hiçbir zaman ayrışmaz. İki liste tutulsaydı biri eskirdi.
 *
 * Tasarım kararları:
 *
 * 1. **En üstte tek cümlelik hüküm.** "Fatura kesilebilir" ya da "kesilemez —
 *    N adım eksik". Kullanıcı sayfayı kaydırmadan cevabı alır.
 *
 * 2. **Her eksik adımın yanında tek düğme: oraya git.** Eksikliği anlatıp
 *    "bulun" demek yetmez; bağlantı doğrudan ilgili sekmeyi açar.
 *
 * 3. **Engel ile uyarı görsel olarak ayrışır.** Shopify eksikse fatura yine
 *    kesilir — bunu kırmızı göstermek gerçek engeli sulandırır.
 *
 * 4. **Worker'ın son ölçümü de görünür.** "Ayarları girdim ama hâlâ kesmiyor"
 *    durumunda kapının ne gördüğünü göstermek, telefonla destek aramaktan
 *    hızlıdır. Ölçüm 60 sn önbellekli — bunu da söyler.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { ArrowRight, CheckCircle2, CircleAlert, CircleDashed, RotateCw } from 'lucide-react';
import { api, type KurulumAdimi } from '../lib/api';
import { Kabuk } from '../components/Kabuk';
import { Kart, KartBasligi, KartGovdesi } from '../components/ui/Kart';
import { Dugme } from '../components/ui/Dugme';
import { DurumRozeti } from '../components/DurumRozeti';
import { HataKutusu, Yukleniyor } from '../components/Durumlar';
import { goreliZaman } from '../lib/format';
import { cn } from '../lib/cn';
import { engelSayisi, uyariSayisi } from '../lib/kurulum';

/** Adım durumu → ikon + ton. Tek eşleme; satırlar kendi rengini uydurmaz. */
function adimGorunumu(adim: KurulumAdimi): {
  Ikon: typeof CheckCircle2;
  sinif: string;
  etiket: string;
} {
  if (adim.durum === 'tamam') {
    return { Ikon: CheckCircle2, sinif: 'text-[var(--state-ok)]', etiket: 'Tamam' };
  }
  if (adim.durum === 'eksik' && adim.engel) {
    return { Ikon: CircleAlert, sinif: 'text-[var(--state-bad)]', etiket: 'Eksik — fatura kesilmez' };
  }
  return { Ikon: CircleDashed, sinif: 'text-[var(--state-wait)]', etiket: 'Eksik — fatura kesilir' };
}

export function Kurulum() {
  const { data, error, isPending, isFetching, refetch } = useQuery({
    queryKey: ['kurulum'],
    queryFn: api.kurulum,
    // Kullanıcı başka sekmede ayar girip buraya dönüyor — tazelensin.
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  const eksikEngel = data ? engelSayisi(data.adimlar) : 0;
  const eksikUyari = data ? uyariSayisi(data.adimlar) : 0;

  return (
    <Kabuk
      baslik="Kurulum"
      kirintilar={[{ etiket: 'Yönetim' }, { etiket: 'Kurulum' }]}
      altBaslik="Fatura kesilebilmesi için gereken adımlar"
      eylemler={
        <Dugme
          gorunum="ikincil"
          ikon={<RotateCw size={15} />}
          bekliyor={isFetching && !isPending}
          onClick={() => void refetch()}
        >
          Yenile
        </Dugme>
      }
    >
      {error !== null && <HataKutusu hata={error} tekrarDene={() => void refetch()} />}

      {isPending ? (
        <Kart>
          <Yukleniyor satir={5} />
        </Kart>
      ) : data ? (
        <>
          {/* ---- Hüküm ---- */}
          <Kart
            className={cn(
              data.faturaKesilebilir ? 'border border-[var(--state-ok)]' : 'border border-[var(--state-bad)]'
            )}
          >
            <KartGovdesi className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex min-w-0 items-start gap-3">
                {data.faturaKesilebilir ? (
                  <CheckCircle2
                    size={28}
                    className="mt-0.5 shrink-0 text-[var(--state-ok)]"
                    aria-hidden="true"
                  />
                ) : (
                  <CircleAlert
                    size={28}
                    className="mt-0.5 shrink-0 text-[var(--state-bad)]"
                    aria-hidden="true"
                  />
                )}
                <div className="flex min-w-0 flex-col gap-1">
                  <h2 className="text-lg font-semibold text-[var(--ink)]">
                    {data.faturaKesilebilir
                      ? 'Sistem fatura kesebilir'
                      : `Fatura kesilemez — ${eksikEngel} adım eksik`}
                  </h2>
                  <p className="max-w-[65ch] text-sm text-[var(--ink-muted)]">
                    {data.faturaKesilebilir
                      ? eksikUyari > 0
                        ? `Fatura kesimi açık. ${eksikUyari} bağlantı eksik kaldı; onlar olmadan da fatura kesilir ama aşağıda ne kaybettiğiniz yazıyor.`
                        : 'Tüm adımlar tamam. Gelen siparişler kuyruğa alınıp faturalanır.'
                      : 'Gelen siparişler kuyrukta BEKLER, hiçbiri kaybolmaz; eksikler tamamlanınca kaldığı yerden devam eder.'}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <DurumRozeti
                  ton={data.ortam === 'live' ? 'danger' : 'info'}
                  etiket={data.ortam === 'live' ? 'CANLI ortam' : 'Test ortamı'}
                />
                {data.faturaSayisi > 0 && (
                  <span className="rakam text-xs text-[var(--ink-faint)]">
                    {data.faturaSayisi} fatura kesildi
                  </span>
                )}
              </div>
            </KartGovdesi>
          </Kart>

          {/* ---- Adımlar ---- */}
          <Kart>
            <KartBasligi
              baslik="Adımlar"
              aciklama="Kırmızı olanlar fatura kesimini durdurur; sarı olanlar eksik kalır ama durdurmaz"
            />
            <ol className="divide-y divide-[var(--line)]">
              {data.adimlar.map((adim, sira) => {
                const { Ikon, sinif, etiket } = adimGorunumu(adim);
                const tamam = adim.durum === 'tamam';
                return (
                  <li key={adim.key} className="flex flex-wrap items-start justify-between gap-3 px-6 py-4">
                    <div className="flex min-w-0 items-start gap-3">
                      <span className="rakam w-5 shrink-0 pt-0.5 text-xs text-[var(--ink-faint)]">
                        {sira + 1}
                      </span>
                      <Ikon size={20} className={cn('mt-px shrink-0', sinif)} aria-hidden="true" />
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="text-sm font-medium text-[var(--ink)]">{adim.etiket}</span>
                        <span
                          className={cn('text-sm', tamam ? 'text-[var(--ink-muted)]' : 'text-[var(--ink)]')}
                        >
                          {adim.mesaj}
                        </span>
                        <span className={cn('text-xs', sinif)}>{etiket}</span>
                      </div>
                    </div>
                    {!tamam && data.canEdit && (
                      <Link
                        to={adim.yol}
                        className="inline-flex items-center gap-1.5 rounded-[var(--radius-sm)] border border-[var(--line-strong)] px-3 py-1.5 text-xs font-medium text-[var(--ink)] hover:bg-[var(--surface-hover)]"
                      >
                        Düzelt
                        <ArrowRight size={13} aria-hidden="true" />
                      </Link>
                    )}
                  </li>
                );
              })}
            </ol>
          </Kart>

          {/* ---- Worker'ın gördüğü ---- */}
          <Kart>
            <KartBasligi
              baslik="Fatura kapısı — anlık ölçüm"
              aciklama="Kontör, Veriban oturumu ve Shopify bağlantısı şimdi ölçüldü (60 sn önbellek). ENGEL varsa hiçbir iş alınmaz."
            />
            <KartGovdesi>
              {data.hazirlik === null ? (
                <p className="text-sm text-[var(--ink-muted)]">Ölçüm yapılamadı — sayfayı yenileyin.</p>
              ) : (
                <div className="flex flex-col gap-3">
                  <p className="text-sm text-[var(--ink-muted)]">
                    {data.hazirlik.hazir ? 'Kapı açık' : 'Kapı KAPALI — iş alınmıyor'} ·{' '}
                    {goreliZaman(data.hazirlik.olcumZamani)}
                  </p>
                  <ul className="flex flex-col gap-2">
                    {data.hazirlik.kontroller.map((k) => (
                      <li key={k.ad} className="flex flex-wrap items-baseline gap-2 text-sm">
                        <DurumRozeti
                          ton={k.durum === 'TAMAM' ? 'success' : k.durum === 'UYARI' ? 'warning' : 'danger'}
                          etiket={k.durum === 'TAMAM' ? 'Tamam' : k.durum === 'UYARI' ? 'Uyarı' : 'Engel'}
                        />
                        <span className="text-[var(--ink)]">{k.mesaj}</span>
                        {k.yapilacak && (
                          <span className="text-xs text-[var(--ink-muted)]">— {k.yapilacak}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </KartGovdesi>
          </Kart>
        </>
      ) : null}
    </Kabuk>
  );
}
